<script lang="ts" setup>
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import { showConfirmDialog, showFailToast, showLoadingToast } from "vant";
import { useI18n } from "vue-i18n";
import { onMounted, ref } from "vue";
import { useMateTrait } from "@/stores/mateTrait";
import { useRoute, useRouter } from "vue-router";
import { usePreviewImg } from "@/use/common";
import { MediaUploadFileItem } from "@/types/component";
import { removeImageBackground } from "@/modules/api";
import { getInitTraitPromptJson } from "@/modules/common";

const { id: traitId } = useRoute().query;

const { t } = useI18n();
const store = useMateTrait();
const router = useRouter();
const showEditPortrait = ref(false);
const isEdit = ref(false);
const traitStyle = ref(1);
const activePortrait = ref<MatePortrait | undefined>({
  promptJson: getInitTraitPromptJson(),
});
const activeName = ref("");

const clickEditName = (item: MatePortrait) => {
  activeName.value = item.name!;
  showEditPortrait.value = true;
  activePortrait.value = item;
};

const onClickSaveName = async () => {
  const toast = showLoadingToast({
    message: t("Message.updatingVerticalPainting"),
    duration: 0,
  });
  try {
    const { mediaId, promptJson } = activePortrait.value!;
    let params = {
      name: activeName.value,
      traitId: Number(traitId),
      mediaId,
      ...promptJson,
    };
    await store.updatePortrait(activePortrait.value!.id!, params);
    activePortrait.value!.name = activeName.value;
  } catch (e) {
    showFailToast(t("Message.failedToUpdateVerticalPainting"));
    throw e;
  } finally {
    toast.close();
    showEditPortrait.value = false;
  }
};

const clickDelete = async (item: MatePortrait) => {
  try {
    await showConfirmDialog({
      title: t("Message.deleteCharacterPortrait"),
      message: t("Message.confirmDeletion", { name: item.name }),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    // 取消删除
    return;
  }
  const toast = showLoadingToast({
    message: t("Message.deletingCharacterPortrait"),
    duration: 0,
  });
  try {
    await store.deletePortrait(item.id!);
  } catch (e) {
    showFailToast(t("Message.failedDeleteCharacterPortrait"));
    throw e;
  } finally {
    toast.close();
  }
};
const showGenerateImgModal = ref(false);

const clickEditPortrait = (item?: MatePortrait) => {
  isEdit.value = !!item;
  if (!item) item = { promptJson: getInitTraitPromptJson() };
  if (Array.isArray(item.promptJson))
    item.promptJson = getInitTraitPromptJson();
  item.promptJson.mateTraitId = item.mateTraitId || Number(traitId);
  if (item.id) {
    item.promptJson.id = item.id;
    item.promptJson.name = item.name;
    item.promptJson.mediaId = item.mediaId;
  }
  activePortrait.value = JSON.parse(JSON.stringify(item));
  showGenerateImgModal.value = true;
};

const clickEditImg = (item: MatePortrait) => {
  router.push(`/generation/trait/portrait-edit?id=${item.id}`);
};

const onSaveConfirm = async (params: any) => {
  let { id: mediaId, faceExpression, pose, clothing, style } = params;
  if (!style) style = activePortrait.value?.mateTrait!.style;
  if (!mediaId) mediaId = activePortrait.value?.mediaId;
  let name =
    activePortrait.value?.name || t("Message.unnamedCharacterPortrait");
  const _params = {
    name,
    traitId: Number(traitId),
    mediaId,
    style,
    faceExpression,
    pose,
    clothing,
  };
  const toast = showLoadingToast({
    message: t("Message.savingCharacterPortrait"),
    duration: 0,
  });
  if (activePortrait.value?.id) {
    _params.name = activePortrait.value.name!;
    await store.updatePortrait(activePortrait.value.id, _params);
  } else {
    await store.createPortrait(_params);
  }
  toast.close();
  reloadList();
};

const reloadList = () => {
  store.getOrFetchPortraitList(Number(traitId));
};

const showImgUploadPopup = ref(false);

const onImgSelect = async ([img]: MediaUploadFileItem[]) => {
  const toast = showLoadingToast({
    message: t("Message.removingCharacterPortraitBackground"),
  });
  try {
    const { data } = await removeImageBackground(img.mediaId);
    activePortrait.value = { promptJson: getInitTraitPromptJson() };
    onSaveConfirm({
      id: data.id,
      ...getInitTraitPromptJson(),
      style: traitStyle.value,
    });
  } catch (e) {
    console.error(e);
  }

  toast.close();
};

onMounted(async () => {
  const toast = showLoadingToast({
    message: t("Message.fetchingCharacterPortraitList"),
    duration: 0,
  });
  await Promise.all([
    store.getOrFetchTraitItem(Number(traitId)),
    store.getOrFetchPortraitList(Number(traitId)),
  ]);
  toast.close();
});
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto]">
    <!-- 顶部导航 -->
    <div class="navbar bg-base-100 h-top-nav">
      <!-- 左 -->
      <div class="navbar-start h-full">
        <button class="btn btn-ghost" @click="$router.back()">
          <light-arrow-icon />
        </button>
      </div>
      <!-- 中：占位 -->
      <div class="navbar-center">
        <span>{{ $t("Texts.characterPortrait") }}</span>
      </div>
      <!-- 右：占位 -->
      <div class="navbar-end"></div>
    </div>

    <div class="text-[#ABABAB] overflow-auto">
      <div class="flex justify-end pr-4 mb-0.5 gap-2">
        <button class="btn btn-xs rounded" @click="showImgUploadPopup = true">
          {{ t("Buttons.upload") }}
        </button>
        <button class="btn btn-xs rounded" @click="clickEditPortrait()">
          {{ t(`trait.new`) }}
        </button>
      </div>
      <div class="grid grid-cols-2 gap-2 p-2">
        <div v-for="item in store.portraitList" :key="item.mediaId">
          <div class="relative">
            <VanImage
              :key="item.mediaId"
              :src="item.media?.url ? usePreviewImg(item.media.url) : ''"
              class="w-full aspect-[3/4]"
              fit="cover"
              crossorigin="anonymous"
            >
              <template #loading>
                <VanLoading color="#fff" size="20" type="spinner" />
              </template>
            </VanImage>
            <p class="absolute bottom-1 left-2 bg-[rgba(0,0,0,0.6)] px-3 py-1">
              {{ item.name }}
            </p>
          </div>
          <div class="flex gap-1 mt-2">
            <button
              class="btn-xs rounded font-normal h-8 btn btn-primary flex-1"
              @click="clickEditPortrait(item)"
            >
              {{ $t("Buttons.editPortrait") }}
            </button>
            <button
              class="btn-xs rounded font-normal h-8 btn btn-primary flex-1"
              @click="clickEditImg(item)"
            >
              {{ $t("Buttons.modifyPicture") }}
            </button>
          </div>
          <div class="flex gap-1 mt-2">
            <button
              class="btn-xs rounded font-normal h-8 flex flex-nowrap justify-center items-center btn flex-1"
              @click="clickEditName(item)"
            >
              {{ $t("Buttons.changeName") }}
            </button>
            <button
              class="btn-xs rounded font-normal h-8 flex flex-nowrap justify-center items-center btn flex-1"
              @click="clickDelete(item)"
            >
              <i class="iconfont icon-delete"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 修改立绘名称 -->
    <VanPopup
      v-model:show="showEditPortrait"
      class="grid grid-rows-[1fr] py-10 px-4"
      position="bottom"
    >
      <div class="flex flex-col gap-4">
        <div class="text-base text-center font-bold">
          {{ activePortrait?.name }}
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex flex-col gap-2">
            <span>{{ $t("Texts.portraitName") }}</span>
            <input v-model="activeName" class="input input-bordered" />
          </div>
        </div>
      </div>
      <div class="flex flex-col items-center gap-2 mt-4">
        <button
          class="btn btn-sm btn-primary w-[200px]"
          @click="onClickSaveName"
        >
          <i class="iconfont icon-user-edit"></i>
          {{ $t("Buttons.save") }}
        </button>
        <button class="btn btn-sm w-[200px]" @click="showEditPortrait = false">
          {{ $t("Buttons.back") }}
        </button>
      </div>
    </VanPopup>

    <ImageModalPortrait
      v-model:show="showGenerateImgModal"
      :req-data="activePortrait!.promptJson"
      @saved="reloadList"
    />

    <MediaSelectPopup
      v-model:show="showImgUploadPopup"
      :max-count="1"
      :multiple="false"
      :upload-type="7"
      enableCrop
      media-type="img"
      replace
      @on-media-selected="onImgSelect"
    />
  </div>
</template>
